import {
  COMPONENTS_PERMISSIONS_BY_MODULE,
  MENU_PERMISSIONS_BY_MODULE,
  ROUTER_PERMISSIONS_BY_MODULE,
  RULES_BY_MODULE,
  SYSTEM_ROUTES,
} from './winnin-auth-permissions.consts'
import type {
  TComponentsPermissions,
  TMenuPermissions,
  TModuleRules,
  TPermissionModule,
  TRouterPermission,
} from './winnin-auth-permissions.types'

function mergeRestrictive<V extends Record<string, boolean>>(list: Record<string, V>[]): Record<string, V> {
  const merged: Record<string, V> = {}
  for (const perms of list) {
    for (const key of Object.keys(perms)) {
      const current = merged[key]
      merged[key] = current
        ? (Object.fromEntries(Object.keys(perms[key]).map(flag => [flag, current[flag] || perms[key][flag]])) as V)
        : { ...perms[key] }
    }
  }
  return merged
}

function resolveModules<T>(modules: string[], map: Partial<Record<TPermissionModule, T>>): T[] {
  return modules.map(moduleName => map[moduleName as TPermissionModule]).filter((perm): perm is T => Boolean(perm))
}

export const getUserRouterPermissions = (modules: string[]): TRouterPermission | undefined => {
  const perms = resolveModules(modules, ROUTER_PERMISSIONS_BY_MODULE)
  if (!perms.length) return undefined
  return {
    default: perms[0].default,
    routes: [...perms.flatMap(perm => perm.routes), ...SYSTEM_ROUTES],
  }
}

export const getUserComponentPermissions = (modules: string[]): TComponentsPermissions | undefined => {
  const perms = resolveModules(modules, COMPONENTS_PERMISSIONS_BY_MODULE)
  if (!perms.length) return undefined
  return {
    brandPageTabs: mergeRestrictive(perms.map(perm => perm.brandPageTabs)),
    brandComponents: mergeRestrictive(perms.map(perm => perm.brandComponents)),
  }
}

export const getUserMenuPermissions = (modules: string[]): TMenuPermissions | undefined => {
  const perms = resolveModules(modules, MENU_PERMISSIONS_BY_MODULE)
  if (!perms.length) return undefined
  return mergeRestrictive(perms) as TMenuPermissions
}

export const getUserRules = (modules: string[]): TModuleRules | undefined => {
  const rulesList = resolveModules(modules, RULES_BY_MODULE)
  if (!rulesList.length) return undefined
  const keys = Object.keys(rulesList[0]) as (keyof TModuleRules)[]
  return keys.reduce((merged, key) => {
    merged[key] = rulesList.some(rules => rules[key])
    return merged
  }, {} as TModuleRules)
}
